/* global debounce, trapFocus */

if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.empty === 'false') this.init();
    }

    init() {
      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };

      this.cartDrawer = document.getElementById('cart-drawer');
      this.itemStatus = document.getElementById('cart-line-item-status');
      this.currentTotalItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value, 10),
        0,
      );

      this.currentQuantities = [];
      this.querySelectorAll('.cart-item').forEach((item) => {
        this.currentQuantities[item.dataset.variantId] = Number(item.querySelector('.qty-input__input').value);
      });

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', debounce(this.handleChange.bind(this)));

      // Check if we're on the main cart page
      const isMainCartPage = this.closest('.cc-main-cart') !== null;

      // Add event listener for Update Cart button and form submission only on main cart page
      if (isMainCartPage) {
        const cartForm = document.querySelector('form.cart-form');
        if (cartForm) {
          cartForm.addEventListener('submit', (evt) => {
            if (evt.submitter && evt.submitter.name === 'update') {
              evt.preventDefault();
              
              // Show loading state
              const updateButton = evt.submitter;
              updateButton.classList.add('loading');
              
              // Get all quantity inputs
              const quantityInputs = cartForm.querySelectorAll('[name="updates[]"]');
              const updates = {};
              
              quantityInputs.forEach((input, index) => {
                updates[index + 1] = parseInt(input.value, 10);
              });

              // Update cart using fetch
              fetch(theme.routes.cartUpdate, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  updates: updates,
                  sections: this.getSectionsToRender().map((section) => section.section)
                })
              })
              .then(response => response.json())
              .then(data => {
                // Show checkout button and hide validation message
                const checkoutButton = document.querySelector('.cart__summary-checkout');
                const validationMsg = document.querySelector('.qty-info-btn');
                if (checkoutButton) checkoutButton.style.display = 'block';
                if (validationMsg) validationMsg.style.display = 'none';

                // Update cart sections
                this.getSectionsToRender().forEach((section) => {
                  const sectionEl = document.getElementById(section.id);
                  if (!sectionEl) return;

                  const { selector } = section;
                  const el = sectionEl.querySelector(selector) || sectionEl;
                  el.innerHTML = CartItems.getElementHTML(data.sections[section.section], selector);
                });

                // Update cart icon bubble with correct quantities
                const cartIconBubble = document.getElementById('cart-icon-bubble');
                if (cartIconBubble) {
                  // Calculate cart count excluding freebie items (price <= 0)
                  let totalQuantity = 0;
                  if (data.items && data.items.length > 0) {
                    data.items.forEach(item => {
                      // Skip freebie items (items with price 0 or negative)
                      if (item.price > 0) {
                        // Check if it's a bundle child item (don't count these)
                        const prodProp = item.properties || {};
                        if (typeof prodProp._boldBuilderId !== "undefined") {
                          // If item has _boldVariantIds, it's a parent bundle (count it)
                          // If item only has _boldBuilderId, it's a child item (don't count it)
                          if (typeof prodProp._boldVariantIds !== "undefined") {
                            totalQuantity += item.quantity;
                          }
                        } else {
                          // Regular item, count it
                          totalQuantity += item.quantity;
                        }
                      }
                    });
                  }
                  
                  cartIconBubble.innerHTML = `<div class="header__cart-count absolute bg-theme-text text-theme-bg text-center">
                    <span aria-hidden="true" class="header__cart-count-number">
                      ${totalQuantity}
                    </span>
                    <span class="visually-hidden">${totalQuantity} items</span>
                  </div>`;
                }

                // Update other cart elements
                this.updateLiveRegions();
                this.currentTotalItemCount = data.item_count;
                
                // Update quantities in memory
                this.querySelectorAll('.cart-item').forEach((item) => {
                  const input = item.querySelector('.qty-input__input');
                  if (input) {
                    this.currentQuantities[item.dataset.variantId] = Number(input.value);
                  }
                });
                
                // Remove loading state
                updateButton.classList.remove('loading');
                
                // Dispatch cart update event
                this.dispatchEvent(
                  new CustomEvent('on:cart:update', {
                    bubbles: true,
                    detail: { cart: data }
                  })
                );
              })
              .catch(error => {
                console.error('Error:', error);
                const errorContainer = document.getElementById('cart-errors');
                if (errorContainer) {
                  errorContainer.textContent = theme.strings.cartError;
                  errorContainer.hidden = false;
                }
                // Remove loading state on error
                updateButton.classList.remove('loading');
              });
            }
          });
        }
      }
    }

    /**
     * Handles 'click' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-remove-item')) return;
      evt.preventDefault();
      let result = ifMainProduct(evt.target);
      this.updateQuantity(evt.target.dataset.index, 0);
    }

    /**
     * Handles 'change' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      // Check if we're on the main cart page
      const isMainCartPage = this.closest('.cc-main-cart') !== null;

      if (isMainCartPage) {
        // For main cart page: show validation message and don't update immediately
        const checkoutButton = document.querySelector('.cart__summary-checkout');
        const validationMsg = document.querySelector('.qty-info-btn');
        if (checkoutButton) checkoutButton.style.display = 'none';
        if (validationMsg) validationMsg.style.display = 'block';
        
        // Don't update quantity immediately
        evt.preventDefault();
        evt.stopPropagation();
      } else {
        // For cart drawer and other locations: update quantity immediately
        this.updateQuantity(evt.target.dataset.index, evt.target.value, document.activeElement.name);
      }
    }

    /**
     * Updates the quantity of a line item.
     * @param {number} line - Line item index.
     * @param {number} quantity - Quantity to set.
     * @param {string} name - Active element name.
     */
    async updateQuantity(line, quantity, name) {
      // Checking if this is a bundle product before showing loader
      const lineItem = document.getElementById(`cart-item-${line}`);
      const isBundleProduct = lineItem && (
        lineItem.dataset.itemProperty !== '[]' || 
        lineItem.querySelector('[data-bundle-product="true"]') ||
        lineItem.classList.contains('bundle-product')
      );

      if (isBundleProduct) {
        document.querySelector('.bundle-loader-custom').style.display = 'block';
        window.hideBundleLoaderSafely();
      }
      
      if (window.bundleOperationInProgress) {
       
        return;
      }

      const cartDrawerContent = this.cartDrawer ? this.cartDrawer.querySelector('.drawer__content') : null;
      const cartDrawerContentScroll = cartDrawerContent ? cartDrawerContent.scrollTop : 0;
      const cartDrawerScroll = this.cartDrawer ? this.cartDrawer.scrollTop : 0;

      this.enableLoading(line);

      // clear all errors except this line's (which will be refreshed after this update)
      const lineErrorsId = `line-item-error-${line}`;
      const lineErrors = document.getElementById(lineErrorsId);
      document.querySelectorAll(`.cart-errors, .cart-item__error:not([id="${lineErrorsId}"])`).forEach((el) => {
        el.innerHTML = '';
        el.hidden = true;
      });

      const sections = this.getSectionsToRender().map((section) => section.section);
      this.fetchRequestOpts.body = JSON.stringify({
        line,
        quantity,
        sections: [...new Set(sections)],
        sections_url: window.location.pathname,
      });

      try {
        const lineItem = document.getElementById(`cart-item-${line}`);
        const variantId = Number(lineItem.dataset.variantId);
        const oldTotalQuantity = this.currentTotalItemCount;
        const response = await fetch(theme.routes.cartChange, this.fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        const newTotalQuantity = data.item_count;

        if (this.cartDrawer) {
          cartDrawerContent.classList.toggle('drawer__content--flex', newTotalQuantity === 0);

          if (newTotalQuantity === 0) {
            const drawerHeader = this.cartDrawer.querySelector('.drawer__header');
            if (drawerHeader) drawerHeader.classList.add('drawer__header-empty');
            const recommendations = this.cartDrawer.querySelector('product-recommendations');
            if (recommendations) recommendations.remove();

            // Appending 0 Cart HTML to Cart Bubble element when cart is emtpy
            const cartIconBubble = document.getElementById('cart-icon-bubble');
            setTimeout(() => {
              cartIconBubble.innerHTML = `<div class="header__cart-count absolute bg-theme-text text-theme-bg text-center" bis_skin_checked="1">
                <span aria-hidden="true" class="header__cart-count-number">
                  0
                </span>
                <span class="visually-hidden">0 items</span>
              </div>`;
            }, 100);
          }
        } else if (newTotalQuantity === 0) {
          // We're on the Cart page
          const cartTitle = this.closest('.cc-main-cart').querySelector('.js-cart-title');
          if (cartTitle) cartTitle.style.textAlign = 'center';

          const cartHeader = document.querySelector('.main__cart-form');
          if (cartHeader) cartHeader.classList.add('main__cart-head-empty');

          const cartSummaryCss = document.getElementById('cart-summary-css');
          if (cartSummaryCss) cartSummaryCss.remove();

          const cartSummary = document.getElementById('cart-summary');
          if (cartSummary) cartSummary.hidden = true;

          // Appending 0 Cart HTML to Cart Bubble element when cart is emtpy
          const cartIconBubble = document.getElementById('cart-icon-bubble');
          setTimeout(() => {
            cartIconBubble.innerHTML = `<div class="header__cart-count absolute bg-theme-text text-theme-bg text-center" bis_skin_checked="1">
                <span aria-hidden="true" class="header__cart-count-number">
                  0
                </span>
                <span class="visually-hidden">0 items</span>
              </div>`;
          }, 100);
        }

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const { selector } = section;
          const el = sectionEl.querySelector(selector) || sectionEl;
          el.innerHTML = CartItems.getElementHTML(data.sections[section.section], selector);
        });

        if (this.cartDrawer && newTotalQuantity === 0) {
          cartDrawerContent.classList.add('grow', 'flex', 'items-center');

          if (this.cartDrawer.querySelector('promoted-products')) {
            this.cartDrawer.querySelector('.drawer__content').classList.toggle('drawer__empty-with-promotions', newTotalQuantity === 0);
          }
        }

        this.updateRecommendations(data.item_count > 0 ? data.items[0].product_id : null);
        this.updateLiveRegions();
        this.setFocus(line, newTotalQuantity, name);
        this.dataset.empty = newTotalQuantity === 0;
        this.currentTotalItemCount = newTotalQuantity;

        // Fire the on:line-item:change event if the line item quantity has changed
        if (oldTotalQuantity !== newTotalQuantity) {
          this.dispatchEvent(
            new CustomEvent('on:line-item:change', {
              bubbles: true,
              detail: {
                cart: data,
                variantId,
                oldQuantity: this.currentQuantities[variantId],
                newQuantity: Number(quantity),
              },
            }),
          );
        }

        this.currentQuantities[variantId] = Number(quantity);

        lineErrors.innerHTML = '';
        lineErrors.hidden = true;
        // hideSubProduct();
      } catch (error) {
        if (/^[0-9]+$/.test(error.message)) {
          lineErrors.textContent = theme.strings.cartError;
        } else {
          lineErrors.textContent = error.message;
        }
        lineErrors.hidden = false;
        console.log(error); // eslint-disable-line

        this.querySelectorAll('.cart-item__loader').forEach((loader) => {
          loader.hidden = true;
        });

        this.dispatchEvent(
          new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: {
              error: error.message,
            },
          }),
        );

        const input = document.getElementById(`quantity-${line}`);
        input.value = input.dataset.initialValue;
        input.closest('quantity-input').currentQty = input.dataset.initialValue;
      } finally {
        this.classList.remove('pointer-events-none');

        // Attempt to maintain the same scroll position in the cart drawer
        if (cartDrawerContent) {
          requestAnimationFrame(() => {
            cartDrawerContent.scrollTop = cartDrawerContentScroll;
          });
          setTimeout(() => {
            cartDrawerContent.scrollTop = cartDrawerContentScroll;
          }, 0);
          requestAnimationFrame(() => {
            this.cartDrawer.scrollTop = cartDrawerScroll;
          });
          setTimeout(() => {
            this.cartDrawer.scrollTop = cartDrawerScroll;
          }, 0);
        }
      }
    }

    /**
     * Refreshes the cart by rerendering its sections and updating its product recommendations.
     */
    async refresh() {
      const errors = document.getElementById('cart-errors');
      try {
        const sections = this.getSectionsToRender().map((section) => section.section);
        const response = await fetch(`?sections=${[...new Set(sections)]}`);

        // The status is 400, refresh the whole cart and stop
        if (response.status === 400 && this.cartDrawer) {
          this.cartDrawer.refresh(true);
          return;
        }

        const data = await response.json();

        if (!response.ok) throw new Error(response.status);

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const el = sectionEl.querySelector(section.selector) || sectionEl;
          el.innerHTML = CartItems.getElementHTML(data[section.section], section.selector);
        });

        const firstCartItem = this.querySelector('.cart-item:first-child');
        this.updateRecommendations(firstCartItem ? firstCartItem.dataset.productId : null);

        errors.innerHTML = '';
        errors.hidden = true;
        // hideSubProduct();
      } catch (error) {
        errors.textContent = theme.strings.cartError;
        errors.hidden = false;
        console.log(error); // eslint-disable-line

        this.dispatchEvent(
          new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: {
              error: error.message,
            },
          }),
        );
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      let sections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
        {
          id: 'free-shipping-notice',
          section: 'free-shipping-notice',
          selector: '.free-shipping-notice',
        },
      ];

      if (this.cartDrawer) {
        const cartDrawerId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: cartDrawerId,
            selector: 'cart-items',
          },
          {
            id: 'cart-promoted-products',
            section: cartDrawerId,
            selector: '#cart-promoted-products',
          },
          {
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '.cart-drawer__summary',
          },
          {
            id: 'cart-drawer-media-promotion',
            section: cartDrawerId,
            selector: '#cart-drawer-media-promotion',
          },
        ];
      } else {
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: this.dataset.section,
            selector: 'cart-items',
          },
          {
            id: 'cart-summary',
            section: document.getElementById('cart-summary').dataset.section,
            selector: '.cart__summary',
          },
        ];
      }

      return sections;
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows a loading icon over a line item.
     * @param {string} line - Line item index.
     */
    enableLoading(line) {
      this.classList.add('pointer-events-none');

      const loader = this.querySelector(`#cart-item-${line} .cart-item__loader`);
      if (loader) loader.hidden = false;

      document.activeElement.blur();
      if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'false');
    }

    /**
     * Updates the cart recommendations.
     * @param {string} productId - The product id for which to find recommendations.
     */
    updateRecommendations(productId) {
      this.recommendations = this.recommendations || document.getElementById('cart-recommendations');
      if (!this.recommendations) return;

      if (productId) {
        this.recommendations.dataset.productId = productId;
        this.recommendations.init();
      } else {
        this.recommendations.innerHTML = '';
      }
    }

    /**
     * Updates the live regions.
     */
    updateLiveRegions() {
      this.itemStatus.setAttribute('aria-hidden', 'true');

      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Traps focus in the relevant container or focuses the active element.
     * @param {number} line - Line item index.
     * @param {number} itemCount - Item count.
     * @param {string} name - Active element name.
     */
    setFocus(line, itemCount, name) {
      const lineItem = document.getElementById(`cart-item-${line}`);
      let activeEl;

      if (lineItem) {
        activeEl = lineItem.querySelector(`[name="${name}"]`);
      }

      if (this.cartDrawer) {
        if (lineItem && activeEl) {
          trapFocus(this.cartDrawer, activeEl);
        } else if (itemCount === 0) {
          trapFocus(this.cartDrawer.querySelector('.js-cart-empty'), this.cartDrawer.querySelector('a'));
        } else if (this.cartDrawer.querySelector('.cart-item')) {
          trapFocus(this.cartDrawer, document.querySelector('.js-item-name'));
        }
      } else if (lineItem && activeEl) {
        activeEl.focus();
      }
    }
  }

  customElements.define('cart-items', CartItems);
}

// hideSubProduct();

async function ifMainProduct(target) {
  const url = target.href;
  const params = new URL(url).searchParams;
  const idParam = params.get('id');
  const productId = idParam;
  
  const isBundleProduct = target.closest('tr').dataset.itemProperty != '[]';
  
  if (isBundleProduct) {
    document.querySelector('.bundle-loader-custom').style.display = 'block';
    await deleteChildProduct(productId);
    
  }
  
  document.dispatchEvent(
    new CustomEvent('dispatch:cart-drawer:refresh', {
      bubbles: true,
    }),
  );
}

async function deleteChildProduct(mainProductId) {
  const storedKeys = JSON.parse(localStorage.getItem('cartKeys')) || [];
  const cartKeys = [mainProductId, ...storedKeys];
 
  try {
    for (const cartKey of cartKeys) {
      const removeUrl = `${window.Shopify.routes.root}cart/change?id=${cartKey}&quantity=0`;
      const response = await fetch(removeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      if (response.ok) {
        
      } else {
        console.error(`Failed to remove product with key ${cartKey}.`);
      }
    }
  
    window.hideBundleLoaderCustom(); 
  } catch (error) {
    console.error('Error deleting items:', error);
  }
}
